import type { ProductKeys } from 'packages/company-hub/dynamic-cards-search-page/src/common/constants/products';

import { universalTypeFilterId } from '../../../../common/constants/filters';
import type { SelectFilterOption } from '../../../filters';
import type { SelectFilterMonad } from '../../../filters/base-select-filter/types';
import type { BooleanFilterMonad } from '../../../filters/boolean-filter/types';
import type { CloudConfig } from '../../../filters/types';

import { filter as assigneeFilter } from './assignee';
import { filter as contentFilter } from './content';
import { filter as contributorFilter } from './contributor';
import { filter as labelFilter } from './label';
import { filter as lastUpdatedFilter } from './last-updated';
import { filter as ownerFilter } from './owner';
import { filter as projectFilter } from './project';
import { filter as spaceFilter } from './space';
import { filter as titleMatchFilter } from './title-match-only';
import { filter as universalTypeFilter } from './universal-type';
import { filter as verifiedFilter } from './verified';

export const allFilters: Readonly<{
	assignee: {
		customValueFields: string[] | undefined;
		getInitialState: (config: CloudConfig) => SelectFilterMonad<'assignee'>;
		id: 'assignee';
		isCompatible: (
			filterState?:
				| {
						id: string;
				  }
				| undefined,
		) => filterState is Readonly<{
			alwaysVisible: boolean;
			customValue?: Record<string, string>;
			customValueFields?: string[];
			didMount: boolean;
			hasSelection: boolean;
			id: 'assignee';
			initialOptions: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			lookup: {
				input: string;
				isActive: boolean;
				isDefined: boolean;
				isLoading: boolean;
				lookupResultOptions: SelectFilterOption[];
				options: SelectFilterOption[];
			};
			options: SelectFilterOption[];
			products: ProductKeys[];
			queryParams: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			selectedOptions: SelectFilterOption[];
			type: 'single-select' | 'multi-select';
			universal: boolean;
			value: string[];
		}>;
		isSupportCustomValue: boolean;
		products: ProductKeys[];
		queryParams: {
			key: string;
		};
		type: 'multi-select';
		universal: boolean;
		withState: (state: unknown, config: CloudConfig) => SelectFilterMonad<'assignee'>;
	};
	content: {
		customValueFields: string[] | undefined;
		getInitialState: (config: CloudConfig) => SelectFilterMonad<'content'>;
		id: 'content';
		isCompatible: (
			filterState?:
				| {
						id: string;
				  }
				| undefined,
		) => filterState is Readonly<{
			alwaysVisible: boolean;
			customValue?: Record<string, string>;
			customValueFields?: string[];
			didMount: boolean;
			hasSelection: boolean;
			id: 'content';
			initialOptions: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			lookup: {
				input: string;
				isActive: boolean;
				isDefined: boolean;
				isLoading: boolean;
				lookupResultOptions: SelectFilterOption[];
				options: SelectFilterOption[];
			};
			options: SelectFilterOption[];
			products: ProductKeys[];
			queryParams: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			selectedOptions: SelectFilterOption[];
			type: 'single-select' | 'multi-select';
			universal: boolean;
			value: string[];
		}>;
		isSupportCustomValue: boolean;
		products: ProductKeys[];
		queryParams: {
			key: string;
		};
		type: 'single-select';
		universal: boolean;
		withState: (state: unknown, config: CloudConfig) => SelectFilterMonad<'content'>;
	};
	contributor: {
		customValueFields: string[] | undefined;
		getInitialState: (config: CloudConfig) => SelectFilterMonad<'contributor'>;
		id: 'contributor';
		isCompatible: (
			filterState?:
				| {
						id: string;
				  }
				| undefined,
		) => filterState is Readonly<{
			alwaysVisible: boolean;
			customValue?: Record<string, string>;
			customValueFields?: string[];
			didMount: boolean;
			hasSelection: boolean;
			id: 'contributor';
			initialOptions: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			lookup: {
				input: string;
				isActive: boolean;
				isDefined: boolean;
				isLoading: boolean;
				lookupResultOptions: SelectFilterOption[];
				options: SelectFilterOption[];
			};
			options: SelectFilterOption[];
			products: ProductKeys[];
			queryParams: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			selectedOptions: SelectFilterOption[];
			type: 'single-select' | 'multi-select';
			universal: boolean;
			value: string[];
		}>;
		isSupportCustomValue: boolean;
		products: ProductKeys[];
		queryParams: {
			key: string;
		};
		type: 'multi-select';
		universal: boolean;
		withState: (state: unknown, config: CloudConfig) => SelectFilterMonad<'contributor'>;
	};
	label: {
		customValueFields: string[] | undefined;
		getInitialState: (config: CloudConfig) => SelectFilterMonad<'label'>;
		id: 'label';
		isCompatible: (
			filterState?:
				| {
						id: string;
				  }
				| undefined,
		) => filterState is Readonly<{
			alwaysVisible: boolean;
			customValue?: Record<string, string>;
			customValueFields?: string[];
			didMount: boolean;
			hasSelection: boolean;
			id: 'label';
			initialOptions: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			lookup: {
				input: string;
				isActive: boolean;
				isDefined: boolean;
				isLoading: boolean;
				lookupResultOptions: SelectFilterOption[];
				options: SelectFilterOption[];
			};
			options: SelectFilterOption[];
			products: ProductKeys[];
			queryParams: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			selectedOptions: SelectFilterOption[];
			type: 'single-select' | 'multi-select';
			universal: boolean;
			value: string[];
		}>;
		isSupportCustomValue: boolean;
		products: ProductKeys[];
		queryParams: {
			key: string;
		};
		type: 'multi-select';
		universal: boolean;
		withState: (state: unknown, config: CloudConfig) => SelectFilterMonad<'label'>;
	};
	lastUpdated: {
		customValueFields: string[] | undefined;
		getInitialState: (config: CloudConfig) => SelectFilterMonad<'lastUpdated'>;
		id: 'lastUpdated';
		isCompatible: (
			filterState?:
				| {
						id: string;
				  }
				| undefined,
		) => filterState is Readonly<{
			alwaysVisible: boolean;
			customValue?: Record<string, string>;
			customValueFields?: string[];
			didMount: boolean;
			hasSelection: boolean;
			id: 'lastUpdated';
			initialOptions: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			lookup: {
				input: string;
				isActive: boolean;
				isDefined: boolean;
				isLoading: boolean;
				lookupResultOptions: SelectFilterOption[];
				options: SelectFilterOption[];
			};
			options: SelectFilterOption[];
			products: ProductKeys[];
			queryParams: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			selectedOptions: SelectFilterOption[];
			type: 'single-select' | 'multi-select';
			universal: boolean;
			value: string[];
		}>;
		isSupportCustomValue: boolean;
		products: ProductKeys[];
		queryParams: {
			key: string;
		};
		type: 'single-select';
		universal: boolean;
		withState: (state: unknown, config: CloudConfig) => SelectFilterMonad<'lastUpdated'>;
	};
	onlyTitleMatch: {
		customValueFields: string[] | undefined;
		getInitialState: (config: CloudConfig) => BooleanFilterMonad<'onlyTitleMatch'>;
		id: 'onlyTitleMatch';
		isCompatible: (
			filterState?:
				| {
						id: string;
				  }
				| undefined,
		) => filterState is Readonly<{
			alwaysVisible: boolean;
			hasSelection: boolean;
			id: 'onlyTitleMatch';
			products: ProductKeys[];
			queryParams: {
				key: string;
			};
			type: 'boolean';
			universal: boolean;
			value: boolean;
		}>;
		isSupportCustomValue: boolean;
		products: ProductKeys[];
		queryParams: {
			key: string;
		};
		type: 'boolean';
		universal: boolean;
		withState: (state: unknown, config: CloudConfig) => BooleanFilterMonad<'onlyTitleMatch'>;
	};
	owner: {
		customValueFields: string[] | undefined;
		getInitialState: (config: CloudConfig) => SelectFilterMonad<'owner'>;
		id: 'owner';
		isCompatible: (
			filterState?:
				| {
						id: string;
				  }
				| undefined,
		) => filterState is Readonly<{
			alwaysVisible: boolean;
			customValue?: Record<string, string>;
			customValueFields?: string[];
			didMount: boolean;
			hasSelection: boolean;
			id: 'owner';
			initialOptions: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			lookup: {
				input: string;
				isActive: boolean;
				isDefined: boolean;
				isLoading: boolean;
				lookupResultOptions: SelectFilterOption[];
				options: SelectFilterOption[];
			};
			options: SelectFilterOption[];
			products: ProductKeys[];
			queryParams: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			selectedOptions: SelectFilterOption[];
			type: 'single-select' | 'multi-select';
			universal: boolean;
			value: string[];
		}>;
		isSupportCustomValue: boolean;
		products: ProductKeys[];
		queryParams: {
			key: string;
		};
		type: 'multi-select';
		universal: boolean;
		withState: (state: unknown, config: CloudConfig) => SelectFilterMonad<'owner'>;
	};
	project: {
		customValueFields: string[] | undefined;
		getInitialState: (config: CloudConfig) => SelectFilterMonad<'project'>;
		id: 'project';
		isCompatible: (
			filterState?:
				| {
						id: string;
				  }
				| undefined,
		) => filterState is Readonly<{
			alwaysVisible: boolean;
			customValue?: Record<string, string>;
			customValueFields?: string[];
			didMount: boolean;
			hasSelection: boolean;
			id: 'project';
			initialOptions: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			lookup: {
				input: string;
				isActive: boolean;
				isDefined: boolean;
				isLoading: boolean;
				lookupResultOptions: SelectFilterOption[];
				options: SelectFilterOption[];
			};
			options: SelectFilterOption[];
			products: ProductKeys[];
			queryParams: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			selectedOptions: SelectFilterOption[];
			type: 'single-select' | 'multi-select';
			universal: boolean;
			value: string[];
		}>;
		isSupportCustomValue: boolean;
		products: ProductKeys[];
		queryParams: {
			key: string;
		};
		type: 'multi-select';
		universal: boolean;
		withState: (state: unknown, config: CloudConfig) => SelectFilterMonad<'project'>;
	};
	space: {
		customValueFields: string[] | undefined;
		getInitialState: (config: CloudConfig) => SelectFilterMonad<'space'>;
		id: 'space';
		isCompatible: (
			filterState?:
				| {
						id: string;
				  }
				| undefined,
		) => filterState is Readonly<{
			alwaysVisible: boolean;
			customValue?: Record<string, string>;
			customValueFields?: string[];
			didMount: boolean;
			hasSelection: boolean;
			id: 'space';
			initialOptions: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			lookup: {
				input: string;
				isActive: boolean;
				isDefined: boolean;
				isLoading: boolean;
				lookupResultOptions: SelectFilterOption[];
				options: SelectFilterOption[];
			};
			options: SelectFilterOption[];
			products: ProductKeys[];
			queryParams: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			selectedOptions: SelectFilterOption[];
			type: 'single-select' | 'multi-select';
			universal: boolean;
			value: string[];
		}>;
		isSupportCustomValue: boolean;
		products: ProductKeys[];
		queryParams: {
			key: string;
		};
		type: 'multi-select';
		universal: boolean;
		withState: (state: unknown, config: CloudConfig) => SelectFilterMonad<'space'>;
	};
	universalTypeFilter: {
		customValueFields: string[] | undefined;
		getInitialState: (config: CloudConfig) => SelectFilterMonad<'universalTypeFilter'>;
		id: 'universalTypeFilter';
		isCompatible: (
			filterState?:
				| {
						id: string;
				  }
				| undefined,
		) => filterState is Readonly<{
			alwaysVisible: boolean;
			customValue?: Record<string, string>;
			customValueFields?: string[];
			didMount: boolean;
			hasSelection: boolean;
			id: 'universalTypeFilter';
			initialOptions: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			lookup: {
				input: string;
				isActive: boolean;
				isDefined: boolean;
				isLoading: boolean;
				lookupResultOptions: SelectFilterOption[];
				options: SelectFilterOption[];
			};
			options: SelectFilterOption[];
			products: ProductKeys[];
			queryParams: {
				isLoading: boolean;
				options: SelectFilterOption[];
			};
			selectedOptions: SelectFilterOption[];
			type: 'single-select' | 'multi-select';
			universal: boolean;
			value: string[];
		}>;
		isSupportCustomValue: boolean;
		products: ProductKeys[];
		queryParams: {
			key: string;
		};
		type: 'single-select';
		universal: boolean;
		withState: (state: unknown, config: CloudConfig) => SelectFilterMonad<'universalTypeFilter'>;
	};
	verified: {
		customValueFields: string[] | undefined;
		getInitialState: (config: CloudConfig) => BooleanFilterMonad<'verified'>;
		id: 'verified';
		isCompatible: (
			filterState?:
				| {
						id: string;
				  }
				| undefined,
		) => filterState is Readonly<{
			alwaysVisible: boolean;
			hasSelection: boolean;
			id: 'verified';
			products: ProductKeys[];
			queryParams: {
				key: string;
			};
			type: 'boolean';
			universal: boolean;
			value: boolean;
		}>;
		isSupportCustomValue: boolean;
		products: ProductKeys[];
		queryParams: {
			key: string;
		};
		type: 'boolean';
		universal: boolean;
		withState: (state: unknown, config: CloudConfig) => BooleanFilterMonad<'verified'>;
	};
}> = Object.freeze({
	[lastUpdatedFilter.id]: lastUpdatedFilter,
	[contributorFilter.id]: contributorFilter,
	[spaceFilter.id]: spaceFilter,
	[verifiedFilter.id]: verifiedFilter,
	[assigneeFilter.id]: assigneeFilter,
	[projectFilter.id]: projectFilter,
	[universalTypeFilterId]: universalTypeFilter,
	[contentFilter.id]: contentFilter,
	[titleMatchFilter.id]: titleMatchFilter,
	[ownerFilter.id]: ownerFilter,
	[labelFilter.id]: labelFilter,
});

export {
	assigneeFilter,
	contentFilter,
	contributorFilter,
	lastUpdatedFilter,
	projectFilter,
	spaceFilter,
	verifiedFilter,
	titleMatchFilter,
	universalTypeFilter,
	ownerFilter,
	labelFilter,
};

export type AllFilterValues = (typeof allFilters)[keyof typeof allFilters];

export type AllFilterStates = ReturnType<
	ReturnType<AllFilterValues['getInitialState']>['getState']
>;

export type Filters = Record<string, AllFilterStates | undefined>;

export type AllFilterKeys = keyof typeof allFilters;
